import { useImperativeHandle, useState, useRef, useCallback } from "react";
import BagBubble from "./BagBubble";
import { getDefaultProductImage } from "@brandclub/common-ui";
import { useTheme } from "@mui/material";
import { appThemeQueryOptions } from "@/api/cache/appThemeQueryOptions";
import { useQuery } from "@tanstack/react-query";
import { useCartCount } from "@/api/cache/cartQueryOptions";

export interface AnimationInstance {
  id: number;
  imageUrl: string;
  color?: string;
}

type AnimationRefObj = {
  current: {
    addToBagAnimation: (
      sku?: string,
      imageBase64?: string,
      successCallback?: () => void
    ) => void;
  } | null;
};

let refs: AnimationRefObj[] = [];

const addNewRef = (newRef: AnimationRefObj["current"]) => {
  refs.push({
    current: newRef,
  });
};

const removeOldRef = (oldRef: AnimationRefObj["current"]) => {
  refs = refs.filter((r) => r.current !== oldRef);
};

const BagButton = ({ onClick }: { onClick: () => Promise<void> }) => {
  const { data: cartCount = 0 } = useCartCount();
  const [animations, setAnimations] = useState<AnimationInstance[]>([]);
  const recieveAnimateRequest = async (sku?: string, imageBase64?: string) => {
    let image = "";
    if (imageBase64) {
      image = imageBase64;
    } else if (sku) {
      image = getDefaultProductImage(sku);
    }

    if (!image) {
      return;
    }

    const newAnimation: AnimationInstance = {
      id: animations.length,

      imageUrl: image,
    };

    setAnimations((prevAnimation) => [...prevAnimation, newAnimation]);
  };

  const animationRef = useRef<AnimationRefObj["current"]>(null);

  const setRef = useCallback((ref: AnimationRefObj["current"]) => {
    if (ref) {
      animationRef.current = ref;
      addNewRef(ref);
    } else {
      removeOldRef(animationRef.current);
    }
  }, []);

  useImperativeHandle(
    setRef,
    useCallback(
      () => ({
        addToBagAnimation: recieveAnimateRequest,
      }),
      [recieveAnimateRequest]
    )
  );
  const { data: styleData } = useQuery(appThemeQueryOptions());
  const componentStyleOverrides = styleData?.componentStyleOverrides;
  const globalButtonRadius = styleData?.globalButtonRadius;

  const bgColor =
    cartCount > 0
      ? componentStyleOverrides?.cartButton?.enabled
        ? componentStyleOverrides?.cartButton?.secondary
        : "var(--bc-color-primary)"
      : "var(--bc-default)";

  const textColor =
    cartCount > 0
      ? componentStyleOverrides?.cartButton?.enabled
        ? componentStyleOverrides?.cartButton?.primary
        : "var(--bc-font-color2)"
      : "var(--bc-color-primary)";

  const getBubbleColor = () => {
    if (componentStyleOverrides?.addToBagBubble?.enabled) {
      return componentStyleOverrides.addToBagBubble.tertiary;
    }

    return "var(--bc-color-secondary)";
  };

  const theme = useTheme() as any;

  return (
    <div
      className={`bc-box-border bc-flex bc-justify-center bc-items-center bc-w-[46px] bc-min-w-[46px] desktop:bc-w-[52px] bc-h-[46px] bc-min-h-[46px] desktop:bc-h-[52px] bc-cursor-pointer bc-rounded-full bc-border`}
      onClick={onClick}
      style={{
        borderRadius: globalButtonRadius,
        borderColor: componentStyleOverrides?.cartButton?.enabled
          ? componentStyleOverrides?.cartButton?.tertiary
          : "var(--bc-color-secondary)",
      }}
      key="bag-button"
    >
      <div
        className={`bc-box-border bc-flex bc-justify-center bc-items-center  bc-w-[32px]  desktop:bc-w-[37px] bc-h-[32px] desktop:bc-h-[37px] bc-rounded-full`}
        style={{
          color: textColor,
          backgroundColor: bgColor,
          fontSize: theme.baseFontSize,
          fontWeight: theme.fontWeight,
          borderRadius: globalButtonRadius,
        }}
      >
        {cartCount}
      </div>
      {animations.map((animation) => {
        return (
          <BagBubble
            key={animation.id}
            color={getBubbleColor()}
            {...animation}
          ></BagBubble>
        );
      })}
    </div>
  );
};

function getRef() {
  const reversePriority = [...refs].reverse();
  const activeRef = reversePriority.find((ref) => ref?.current !== null);
  if (!activeRef) {
    return null;
  }
  return activeRef.current;
}

BagButton.AnimateAddProductToBag = (sku?: string, imageBase64?: string) => {
  getRef()?.addToBagAnimation(sku, imageBase64);
};

export default BagButton;
